import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import type { Me } from '../lib/types';
import { Mark } from '../components/Mark';

const MESSAGES: Record<string, string> = {
  invalid_credentials: 'E-posta veya parola hatalı.',
  too_many_attempts: 'Çok fazla başarısız deneme. 15 dakika sonra tekrar deneyin.',
  password_login_disabled: 'Parolayla giriş kapalı. Google ile giriş yapın.',
};

/** Errors the Google callback reports back as /?login_error=<code>. */
const GOOGLE_ERRORS: Record<string, string> = {
  not_invited:
    'Bu Google hesabı için erişim izni yok. Bir yöneticiden bu e-posta adresine davet göndermesini isteyin.',
  account_disabled: 'Bu hesap bir yönetici tarafından devre dışı bırakıldı.',
  account_link_conflict:
    'Bu e-posta adresi başka bir Google hesabına bağlı. Daha önce kullandığınız Google hesabıyla giriş yapın.',
  state_mismatch: 'Giriş oturumunun süresi doldu veya başka bir sekmede başlatıldı. Lütfen tekrar deneyin.',
  cancelled: 'Google ile giriş iptal edildi.',
  google_failed: 'Google ile giriş doğrulanamadı. Lütfen tekrar deneyin.',
};

interface AuthConfig {
  google: boolean;
  password: boolean;
}

function readLoginError(): string | null {
  const code = new URLSearchParams(location.search).get('login_error');
  if (!code) return null;
  history.replaceState(null, '', location.pathname);
  return GOOGLE_ERRORS[code] ?? 'Giriş yapılamadı.';
}

export function Login({ onLogin }: { onLogin: (me: Me) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(() => readLoginError());
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState<AuthConfig | null>(null);

  useEffect(() => {
    api
      .get<AuthConfig>('/v1/auth/config')
      .then(setConfig)
      .catch(() => setConfig({ google: false, password: true }));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/v1/auth/login', { email, password, client: 'web' });
      onLogin(await api.get<Me>('/v1/me'));
    } catch (err) {
      setError(
        err instanceof ApiError ? (MESSAGES[err.code] ?? 'Giriş yapılamadı.') : 'Sunucuya ulaşılamadı.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <form onSubmit={submit} aria-labelledby="login-title">
        <div className="brand" style={{ marginBottom: 'var(--s5)' }}>
          <Mark /> Converoom
        </div>
        <h1 id="login-title">Giriş yapın</h1>
        <p className="lede">Hesaplar yalnızca davetle açılır.</p>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        {config?.google && (
          <a
            className="btn primary google-btn"
            href="/v1/auth/google/start"
            style={{ width: '100%', justifyContent: 'center' }}
          >
            <GoogleMark /> Google ile giriş yap
          </a>
        )}
        {config?.google && config.password && (
          <p className="divider" aria-hidden="true">
            veya
          </p>
        )}
        {config?.password && (
          <>
            <label className="field">
              <span>E-posta</span>
              <input
                className="input"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Parola</span>
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <button
              className={config.google ? 'btn' : 'btn primary'}
              type="submit"
              disabled={busy}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              {busy ? 'Giriş yapılıyor…' : 'Parolayla giriş yap'}
            </button>
          </>
        )}
      </form>
    </main>
  );
}

/** Google "G" mark (brand asset used only to label the Google sign-in button). */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" style={{ marginRight: 8 }}>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
